import React, { useState, useCallback, useMemo } from "react";
import { CommentItem } from "./CommentItem";
import { ChevronDown, ChevronRight } from "lucide-react";

const MAX_NESTING_DEPTH = 3;

const NestedComments = React.memo(function NestedComments({
  comments,
  parentId = null,
  depth = 0,
  currentUserId,
  currentUserEmail,
  currentUserUsername,
  onReply,
  onEdit,
  onLike,
  onView,
  showCommentEditor,
  replyingTo,
  onComment,
  backendUrl,
  profilePicture,
  avatar,
  handleSeen,
  handleRemoveFile,
  onDeleteComment,
  setIsEditing,
  isEditing,
}) {
  const [collapsedThreads, setCollapsedThreads] = useState(new Set());

  // Filter comments to show only direct replies to this parent
  const directReplies = useMemo(
    () =>
      comments.filter((comment) =>
        parentId === null
          ? !comment.parent_id || comment.parent_id === null
          : comment.parent_id === parentId
      ),
    [comments, parentId]
  );

  // Get child comments for a specific comment
  const getChildComments = useCallback(
    (commentId) =>
      comments.filter((comment) => comment.parent_id === commentId),
    [comments]
  );

  const toggleCollapse = useCallback((commentId) => {
    setCollapsedThreads((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  }, []);

  const getIndentStyle = (depth) => ({
    marginLeft: `${Math.min(depth * 24, 72)}px`, // Max indent of 72px
    borderLeft: depth > 0 ? "2px solid #e5e7eb" : "none",
    paddingLeft: depth > 0 ? "12px" : "0",
  });

  if (directReplies.length === 0) return null;

  return (
    <div className="space-y-3">
      {directReplies.map((comment) => {
        const childComments = getChildComments(comment.id);
        const hasChildren = childComments.length > 0;
        const isCollapsed = collapsedThreads.has(comment.id);
        const canNest = depth < MAX_NESTING_DEPTH;
        return (
          <div key={comment.id} style={getIndentStyle(depth)}>
            <div className="flex items-start gap-2">
              {hasChildren && (
                <button
                  onClick={() => toggleCollapse(comment.id)}
                  className="mt-4 p-1 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label={
                    isCollapsed ? "Expand replies" : "Collapse replies"
                  }
                >
                  {isCollapsed ? (
                    <ChevronRight size={14} />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                </button>
              )}

              <div className="flex-1">
                <CommentItem
                  comment={comment}
                  currentUser={currentUserId}
                  onReply={onReply}
                  onEdit={onEdit}
                  onLike={(_, commentId) => onLike(_, commentId)}
                  onView={onView}
                  isLiked={
                    comment.likes?.some(
                      (like) => like?.user?.username === currentUserUsername
                    ) ?? false
                  }
                  isReplying={
                    replyingTo?.user?.username === comment?.user?.username &&
                    showCommentEditor
                  }
                  backendUrl={backendUrl}
                  profilePicture={profilePicture}
                  avatar={avatar}
                  handleSeen={handleSeen}
                  handleRemoveFile={handleRemoveFile}
                  onDeleteComment={onDeleteComment}
                  setIsEditing={setIsEditing}
                  isEditing={isEditing}
                />

                {/* Reply indicator for deep nesting */}
                {hasChildren && !canNest && !isCollapsed && (
                  <div className="mt-2 p-2 bg-gray-50 rounded-lg border">
                    <button
                      onClick={() => {
                        /* Navigate to full thread view */
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Ver {childComments.length} respuesta
                      {childComments.length > 1 ? "s" : ""} más →
                    </button>
                  </div>
                )}

                {/* Nested replies */}
                {hasChildren && !isCollapsed && canNest && (
                  <div className="mt-3">
                    <NestedComments
                      comments={comments}
                      parentId={comment.id}
                      depth={depth + 1}
                      currentUserId={currentUserId}
                      currentUserEmail={currentUserEmail}
                      currentUserUsername={currentUserUsername}
                      onReply={onReply}
                      onEdit={onEdit}
                      onLike={onLike}
                      onView={onView}
                      showCommentEditor={showCommentEditor}
                      replyingTo={replyingTo}
                      onComment={onComment}
                      backendUrl={backendUrl}
                      profilePicture={profilePicture}
                      avatar={avatar}
                      handleSeen={handleSeen}
                      handleRemoveFile={handleRemoveFile}
                      onDeleteComment={onDeleteComment}
                      setIsEditing={setIsEditing}
                      isEditing={isEditing}
                    />
                  </div>
                )}

                {/* Collapsed indicator */}
                {hasChildren && isCollapsed && (
                  <div className="mt-2 text-sm text-gray-500">
                    {childComments.length} respuesta
                    {childComments.length > 1 ? "s" : ""} oculta
                    {childComments.length > 1 ? "s" : ""}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
});

export { NestedComments };
